import * as Sentry from '@sentry/react';

let isInitialized = false;

export const initializeSentry = (store) => {
	console.log('initializing sentry');
	if (isInitialized || import.meta.env.DEV) {
		console.log('Sentry already initialized or in dev mode');
		return;
	}

	const dsn = import.meta.env.VITE_SENTRY_DSN;
	if (!dsn) {
		console.warn('Sentry DSN not provided, skipping initialization');
		return;
	}

	Sentry.init({
		dsn,
		integrations: [Sentry.browserTracingIntegration()],
		environment: import.meta.env.VITE_API_ENVIRONMENT || 'development',
		release: import.meta.env.VITE_APP_VERSION || '1.0.0',
		tracesSampleRate: 0.0, // Only capture errors, no performance transactions
		replaysSessionSampleRate: 0.0, // Disable session replays
		replaysOnErrorSampleRate: 0.0, // Disable error replays
		// Enable source maps for better error tracking
		attachStacktrace: true,
		includeLocalVariables: true,
		beforeSend(event) {
			console.log('🚀 Sentry beforeSend triggered!');

			// Only send error events, not transactions or other event types
			// if (event.type !== 'error' && event.type !== 'transaction') {
			// 	console.log('🚫 Sentry: Filtered out non-error event:', event.type);
			// 	return null;
			// }

			// Add organization context if available
			const state = store?.getState();
			const organization = state?.header?.organization;
			const organizationName = organization?.name;

			console.log('🔍 Sentry Debug - Current state:');
			console.log('  - Redux store available:', !!store);
			console.log('  - Redux state:', state ? 'available' : 'not available');
			console.log('  - Organization object:', organization);
			console.log('  - Organization name:', organizationName);
			console.log('  - Current URL:', window.location.pathname);

			// Set organization tag from Redux store or URL
			if (organizationName) {
				event.tags = {
					...event.tags,
					organization: organizationName,
				};
				console.log(
					'✅ Sentry: Tagged with organization from Redux:',
					organizationName
				);
			} else {
				// Fallback to extracting from URL
				const urlPath = window.location.pathname;
				event.tags = {
					...event.tags,
					organization: urlPath,
				};
				console.log('⚠️ Sentry: Using URL as fallback organization:', urlPath);
			}

			// Add error context
			event.tags = {
				...event.tags,
				environment: import.meta.env.VITE_API_ENVIRONMENT || 'development',
			};

			console.log('📤 Sentry: Final event tags being sent:', event.tags);
			console.log('📤 Sentry: Event summary:', {
				type: event.type,
				message: event.message,
				timestamp: event.timestamp,
				level: event.level,
				platform: event.platform,
			});

			return event;
		},
		beforeSendTransaction() {
			// Don't send any transaction events, only errors
			return null;
		},
	});

	isInitialized = true;
	console.log('Sentry initialized for error tracking');
};

// Function to update organization context when it changes in the app
export const updateSentryOrganization = (organizationName) => {
	if (!isInitialized) {
		console.log('⚠️ Sentry: Not initialized, skipping organization update');
		return;
	}

	console.log('🏢 Sentry: Updating organization context to:', organizationName);

	Sentry.setTag('organization.name', organizationName);
	Sentry.setContext('organization', {
		name: organizationName,
		updated_at: new Date().toISOString(),
	});

	console.log('✅ Sentry: Organization context updated');
};

// Function to capture errors with additional context
export const captureError = (error, context = {}) => {
	if (!isInitialized) {
		console.error('Sentry not initialized, logging error:', error);
		return;
	}

	Sentry.withScope((scope) => {
		Object.keys(context).forEach((key) => {
			scope.setContext(key, context[key]);
		});
		Sentry.captureException(error);
	});
};

// Debug function to check current Sentry state
export const debugSentryState = () => {
	console.log('🔧 Sentry Debug State:');
	console.log('  - Initialized:', isInitialized);
	console.log('  - DSN configured:', !!import.meta.env.VITE_SENTRY_DSN);
	console.log('  - Environment:', import.meta.env.VITE_API_ENVIRONMENT);

	if (isInitialized) {
		console.log('  - Current user:', Sentry.getCurrentScope().getUser());
		console.log('  - Current tags:', Sentry.getCurrentScope().getTags());
		console.log('  - Current context:', Sentry.getCurrentScope().getContext());
	}
};

export default Sentry;
